import { FC, useEffect, useState } from "react";
import { Button, message, Modal, Space, Tooltip, Typography } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { useGetOrderPendingPayment, useGetPayoutPendingSummary, useCreatePayout, useGetPayoutBatchSummary } from "hooks/useFactory";
import { useQueryClient } from "@tanstack/react-query";
import { GetOrderInProgress } from "services/interface";
import { formatDateAndTime } from "utils/convertData";
import { numberFormatePrice } from "utils/formateNumber";
import TableList from "./TableList";
import styled from "@emotion/styled";

interface TablePendingPaymentProps {
  startDate: string;
  endDate: string;
}

const ellipsisStyle: React.CSSProperties = {
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
};

const truncateText = (text: string | undefined) => {
  if (!text) return <Typography.Text>-</Typography.Text>;
  return (
    <Tooltip title={text}>
      <Typography.Text ellipsis style={ellipsisStyle}>
        {text}
      </Typography.Text>
    </Tooltip>
  );
};

const TablePendingPayment: FC<TablePendingPaymentProps> = ({
  startDate,
  endDate,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<GetOrderInProgress[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [warningModalOpen, setWarningModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data } = useGetOrderPendingPayment({
    start_date: startDate,
    end_date: endDate,
    page_number: page,
    row_per_page: pageSize,
    sort_by: "order_code",
    sort_direction: "desc",
  });

  const { data: pendingSummary } = useGetPayoutPendingSummary();
  const { data: batchSummary } = useGetPayoutBatchSummary();
  const { mutate: doCreatePayout, isPending: isCreating } = useCreatePayout();

  useEffect(() => {
    if (data) setTableData(data.data.data);
  }, [data]);

  const handleConfirm = () => {
    doCreatePayout({}, {
      onSuccess: () => {
        message.success("ยืนยันยอดโอนสำเร็จ");
        setModalOpen(false);
        queryClient.invalidateQueries({ queryKey: ["payoutPendingSummary"] });
        queryClient.invalidateQueries({ queryKey: ["payoutBatchSummary"] });
        queryClient.invalidateQueries({ queryKey: ["orderPendingPayment"] });
        queryClient.invalidateQueries({ queryKey: ["orderPendingPaymentSummary"] });
      },
      onError: () => {
        message.error("เกิดข้อผิดพลาด กรุณาลองใหม่");
      },
    });
  };

  const columns: ColumnsType<GetOrderInProgress> = [
    {
      title: "หมายเลขคำสั่งซื้อ",
      dataIndex: "order_code",
      width: 150,
      render: (_text, record) => truncateText(record.order_code),
    },
    {
      title: "สินค้า",
      dataIndex: "product_name",
      align: "left",
      render: (_text, record) => (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          {truncateText(record.product_name)}
          {record.sku_description && (
            <Typography.Text type="secondary" ellipsis style={ellipsisStyle}>
              <Tooltip title={record.sku_description}>({record.sku_description})</Tooltip>
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "จำนวนสินค้า",
      dataIndex: "item_qty",
      width: 90,
      align: "center",
      render: (_text, record) => (
        <Typography.Text>{record.item_qty}</Typography.Text>
      ),
    },
    {
      title: (
        <span style={{ whiteSpace: "normal" }}>
          ราคารวมที่โอนให้
          <br />
          ผู้ประกอบการรวม vat
        </span>
      ),
      dataIndex: "total_base_factory_price",
      width: 150,
      align: "center",
      render: (_text, record) => (
        <Typography.Text>
          {numberFormatePrice(record.total_base_factory_price ?? 0)}
        </Typography.Text>
      ),
    },
    {
      title: "วันที่สั่งซื้อ",
      dataIndex: "order_date",
      align: "center",
      width: 120,
      render: (_text, record) => (
        <Typography.Text>
          {formatDateAndTime(record.order_date)}
        </Typography.Text>
      ),
    },
    {
      title: "วันที่ชำระเงิน",
      dataIndex: "payment_date",
      align: "center",
      width: 120,
      render: (_text, record) => (
        <Typography.Text>
          {record.payment_date ? formatDateAndTime(record.payment_date) : "-"}
        </Typography.Text>
      ),
    },
  ];

  const totalItems = data?.data.pagination.total ?? 0;

  return (
    <CardWrapperTable>
      <div style={{ padding: "0 10px", display: "flex", justifyContent: "space-between" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
        <TableTitle>
          รายการคำสั่งที่รอกดยืนยันยอดโอน ({totalItems})
        </TableTitle>
        <ConditionDescription>
        <ConditionItem color="#faad14">
          หากมีข้อส่งสัยสามารถติดต่อได้ที่{" "}
          <Typography.Link
            href="mailto:contact@bangbowplatform.com"
            style={{ textDecoration: "underline" }}
          >
            contact@bangbowplatform.com
          </Typography.Link>
        </ConditionItem>
      </ConditionDescription>
      </div>
      <div style={{ padding: "10px", display: "flex", justifyContent: "flex-end" }}>
        <div style={{ marginBottom: 6 }}>
          <Button
            type="primary"
            onClick={() => {
              const noReceiptCount = batchSummary?.data.paid.no_receipt_count ?? 0;
              if (noReceiptCount > 0) {
                setWarningModalOpen(true);
              } else {
                setModalOpen(true);
              }
            }}
            disabled={totalItems === 0}
          >
            ยืนยันยอดโอน
          </Button>
        </div>
        </div>
        </div>

      <TableList
        data={tableData}
        columns={columns}
        total={totalItems}
        current={page}
        pageSize={pageSize}
        onPageChange={(p, size) => {
          setPage(p);
          setPageSize(size);
        }}
      />

      <Modal
        title="ยืนยันยอดโอน"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleConfirm}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
        confirmLoading={isCreating}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text>
            ต้องการยืนยันการสร้างรายการขอเบิกเงินใช่หรือไม่?
          </Typography.Text>
          <SummaryRow>
            <Typography.Text type="secondary">จำนวนรายการ</Typography.Text>
            <Typography.Text strong>
              {pendingSummary?.data.item_count ?? 0} รายการ
            </Typography.Text>
          </SummaryRow>
          <SummaryRow>
            <Typography.Text type="secondary">ยอดรวมทั้งหมด</Typography.Text>
            <Typography.Text strong style={{ color: "#1d39c4" }}>
              {numberFormatePrice(pendingSummary?.data.total_amount ?? 0)} บาท
            </Typography.Text>
          </SummaryRow>
        </Space>
      </Modal>

      <Modal
        title={
          <Space>
            <ExclamationCircleFilled style={{ color: "#faad14" }} />
            ไม่สามารถยืนยันยอดโอนได้
          </Space>
        }
        open={warningModalOpen}
        onCancel={() => setWarningModalOpen(false)}
        onOk={() => setWarningModalOpen(false)}
        okText="รับทราบ"
        cancelButtonProps={{ style: { display: "none" } }}
        width={400}
      >
        <Typography.Text>
          กรุณาอัพโหลดใบเสร็จรับเงินให้ครบทุกรายการก่อนยืนยันยอดโอน
          ({batchSummary?.data.paid.no_receipt_count ?? 0} รายการที่ยังไม่อัพโหลด)
        </Typography.Text>
      </Modal>
    </CardWrapperTable>
  );
};

export default TablePendingPayment;

const CardWrapperTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  background-color: #ffffff;
  padding: 5px;
`;

const TableHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 10px 10px 0 10px;
`;

const TableTitle = styled.p`
  font-size: 16px;
  font-weight: bold;
  margin: 0;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
`;


const ConditionDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 10px 10px;
`;

const ConditionItem = styled.p<{ color: string }>`
  font-size: 14px;
  color: #555;
  margin: 0;
  &::before {
    content: "●";
    color: ${({ color }) => color};
    margin-right: 6px;
  }
`;


